import { Component, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { TranslationService } from '../../../shared/translation.service';
import { SortableFilter, SortDirection } from '../../../common/sortable-filter';
import { ProductSetService } from '../../../shared/product-set.service';
import { ProductSetType } from '../product-set-type.enum';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as XLSX from 'xlsx';
import { finalize } from 'rxjs';
import { ProductsService } from 'src/app/shared/products.service';
import { HttpErrorResponse } from '@angular/common/http';
import { isNullOrWhitespace } from 'src/app/common/functions';

@Component({
    selector: 'hc-import-product-set-dialog',
    templateUrl: './import-product-set-dialog.component.html',
    styleUrls: []
})
export class ImportProductSetDialogComponent implements OnInit {

    public nameFormControl = new FormControl('', [Validators.required]);
    public typeFormControl = new FormControl(0);
    public maxMembers = 50000;
    public productIDs: any[] = [];
    public productNumbers: string[] = [];
    public inProgress = false;
    public isError = false;
    public productSets = [];
    public productSetSelectControl = new FormControl(this.productSets[0], [Validators.required]);

    constructor(
        private dialogRef: MatDialogRef<ImportProductSetDialogComponent>,
        private trans: TranslationService,
        private service: ProductSetService,
        private snackbar: MatSnackBar,
        private productService: ProductsService,
        private dialog: MatDialog,
    ) {
        this.productSets =  [
            { description: this.trans.instant('admin.web.create-new-product-set'), value: -2 }
        ];
    }



    ngOnInit() {
        this.inProgress = true;
        let filter = new SortableFilter('Name', SortDirection.asc);
        filter.pageSize = 1000;
        this.service.select(filter).subscribe(res => {
            res.items.forEach(productSet => {
                this.productSets.push({
                    description: productSet.type === ProductSetType.System ? this.trans.instant(productSet.name) : productSet.name,
                    value: productSet.productSetId
                });
            });
            this.inProgress = false;
        });
    }

    private confirm() {
        if(!this.productIDs || this.productIDs?.length == 0){
            this.snackbar.open(this.trans.instant('admin.web.missing-products'), this.trans.instant('admin.web.close'));
            return;
        }
        this.inProgress = true;
        if (this.isNameInputVisible()) {
            this.importNew();
        } else {
           this.importToExisting();
        }
    }

    private close() {
        this.dialogRef.close();
    }

    private isNameInputVisible(): boolean {
        return this.productSetSelectControl.value === this.productSets[0];
    }

    async onFileSelected(event: Event) {
        this.productNumbers= [];

        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        const ext = (file.name.split('.').pop() || '').toLowerCase();
        try {
            if (ext === 'csv') {
                const text = await file.text();
                this.productNumbers = this.parseCsvToIds(text);
            } else if (ext === 'xlsx') {
                const buf = await file.arrayBuffer();
                this.productNumbers = this.parseXlsxToIds(buf);
            } else {
                this.productNumbers = [];
                this.snackbar.open(this.trans.instant('admin.web.empty-file'), this.trans.instant('admin.web.close'));
                return;
            }

            this.productService.getProductsByNumber(this.productNumbers)
                .subscribe({
                    next: (ids: number[]) => {
                        this.productIDs = ids;
                    },
                    error: err => console.error(err)
                });
            // Handle duplicity
            this.productIDs = Array.from(new Set(this.productIDs));
        } finally {
            input.value = '';
        }
    }

    private parseCsvToIds(text: string): string[] {
        const clean = text.replace(/^\uFEFF/, '');
        const lines = clean.split(/\r?\n/);
        const ids: string[] = [];

        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;

            const cell = trimmed.split(/[;,]/)[0]?.trim();
            if (!cell) continue;

            ids.push(cell);
        }
        return ids;
    }

    private parseXlsxToIds(buf: ArrayBuffer): string[] {
        const wb = XLSX.read(buf, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];

        const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true }) as any[][];
        const ids: string[] = [];

        for (const r of rows) {
            const v = (r?.[0] ?? '').toString().trim();
            if (!v) continue;

            ids.push(v);
        }
        return ids;
    }

    public importNew(){

        if(isNullOrWhitespace(this.nameFormControl.value)){
            this.snackbar.open(`${this.trans.instant('admin.web.name-empty')}`, '', { duration: 4000 });
            this.inProgress = false;
            return;
        }

        this.service.create(this.nameFormControl.value, this.typeFormControl.value, this.productIDs)
            .pipe(finalize(() => this.inProgress = false))
            .subscribe({
                next: ({ inserted, notFound }) => {
                this.isError = false;
                this.snackbar.open(`${this.trans.instant('admin.web.list-created')} (${inserted} OK, ${notFound} ${this.trans.instant('admin.web.not-found') ?? 'nenalezeno'})`, '', { duration: 4000 });
                this.dialogRef.close({ inserted, notFound });
                },
                error: (err: HttpErrorResponse) => {
                this.isError = true;

                const msg =
                    typeof err === 'string' ? err : this.trans.instant('admin.web.list-create-error');

                    this.snackbar.open('Error: ' + msg, '', { duration: 4000 });
                }
            });
 
    }   

    public importToExisting(){
        if(this.productSetSelectControl.value == null){
            this.snackbar.open(`${this.trans.instant('admin.web.product-set-empty')}`, '', { duration: 4000 });
            this.inProgress = false;
            return;
        }

        this.service.addToProductSetList(this.productSetSelectControl.value.value, this.productIDs)
            .subscribe(res => {
                const inserted = (res as any).inserted ?? (res as any).Inserted ?? 0;
                const notFound = (res as any).notFound ?? (res as any).NotFound ?? 0;
                this.inProgress = false;
                this.isError = false;
                this.snackbar.open(`${this.trans.instant('admin.web.saved')} (${inserted} OK, ${notFound} ${this.trans.instant('admin.web.not-found')})`, '', {duration: 4000});
            },
            err => {
                this.inProgress = false;
                this.isError = true;
                this.snackbar.open(this.trans.instant('admin.web.error-saving'), '', {duration: 4000});
            },
        () => {
            this.dialogRef.close();
        });
    } 
}

export interface ProductSetInsertResult {
  inserted: number;
  notFound: number;
}
